/**
 * Tracks the save reset gate for the current campaign launch. Pavilion owns the
 * matchmaking decision; this state object keeps save transitions in one place.
 * Deadlines are external: GameLaunchCoordinator reports lookup/native/stop/launch
 * failures through campaignSwitchFailed; Pavilion clears this gate on that event,
 * campaign removal, or its prewarming launch/macro timeout. Native restore has its
 * own bounded reply deadline, so Pavilion pauses the launch timer during restore.
 *
 * `configuration` tri-state:
 * - `false` — no save config (idle / cleared / v2 unconfigured). Does not block
 *   matchmaking. Default so a fresh host process does not advertise
 *   `game_save_restore` before any campaign is assigned.
 * - `null` — unknown during an in-flight launch/reset lookup. Blocks until
 *   `configurationChecked`.
 * - `true` — campaign has a save config; full restore/launch handshake required.
 *
 * Idle `false` is not the same as an unconfigured campaign that finished launch:
 * `unconfiguredLaunchComplete` tracks the latter so late game-ready events are
 * rejected while idle / after clear().
 */
export class GameSaveLifecycleState {
  private configuration: boolean | null = false;
  private restorePending = false;
  private awaitingFreshLaunch = false;
  private resetInProgress = false;
  private sessionDirty = false;
  private dispatchedLaunch: { campaignId: string; generation: number; confirmed: boolean } | null = null;
  private readyBeforeLaunchConfirmation = false;
  private readyConsumed = false;
  private unconfiguredLaunchComplete = false;

  beginCampaignLaunch(): void {
    this.configuration = null;
    this.restorePending = true;
    this.awaitingFreshLaunch = true;
    this.resetInProgress = false;
    this.sessionDirty = false;
    this.dispatchedLaunch = null;
    this.readyBeforeLaunchConfirmation = false;
    this.readyConsumed = false;
    this.unconfiguredLaunchComplete = false;
  }

  beginRestore(): void {
    this.restorePending = true;
    this.awaitingFreshLaunch = true;
    this.dispatchedLaunch = null;
    this.readyBeforeLaunchConfirmation = false;
    this.readyConsumed = false;
    this.unconfiguredLaunchComplete = false;
  }

  /** Returns false if a required reset has lost its save configuration. */
  configurationChecked(configured: boolean): boolean {
    if (!configured && this.resetInProgress) return false;
    this.configuration = configured;
    if (!configured) this.restorePending = false;
    return true;
  }

  restoreCompleted(): void {
    this.restorePending = false;
  }

  /** Only a launch command issued after restore can supply a fresh game. */
  launchDispatched(campaignId: string, generation: number): void {
    if (this.configuration !== true || this.restorePending) return;
    this.dispatchedLaunch = { campaignId, generation, confirmed: false };
    this.awaitingFreshLaunch = true;
    this.readyBeforeLaunchConfirmation = false;
    this.readyConsumed = false;
  }

  /** The launch reply can only release readiness from the matching command. */
  launchConfirmed(campaignId: string, generation: number): boolean {
    if (this.dispatchedLaunch?.campaignId !== campaignId ||
        this.dispatchedLaunch.generation !== generation || this.readyConsumed) return false;
    this.dispatchedLaunch.confirmed = true;
    const ready = this.readyBeforeLaunchConfirmation && !this.awaitingFreshLaunch;
    this.readyBeforeLaunchConfirmation = false;
    return ready;
  }

  freshGameLaunched(): void {
    if (this.configuration !== null && !this.restorePending &&
        (this.configuration === false || this.dispatchedLaunch !== null) && this.awaitingFreshLaunch) {
      this.awaitingFreshLaunch = false;
      this.readyBeforeLaunchConfirmation = false;
      if (this.configuration === false)
        this.unconfiguredLaunchComplete = true;
    }
  }

  readyForPlayers(): boolean {
    // No active launch means no save operation is blocking. Keep late ready
    // events rejected by canAcceptGameReady(), and let Pavilion report idle/errors.
    if (this.configuration === null)
      return !this.restorePending && !this.awaitingFreshLaunch && !this.resetInProgress;
    return !this.restorePending &&
      !this.awaitingFreshLaunch && !this.resetInProgress &&
      (this.configuration === false || (this.readyConsumed && this.dispatchedLaunch?.confirmed === true));
  }

  canAcceptGameReady(): boolean {
    return this.configuration !== null && !this.restorePending && !this.awaitingFreshLaunch &&
      (this.configuration === false
        ? this.unconfiguredLaunchComplete
        : (!this.readyConsumed && this.dispatchedLaunch?.confirmed === true));
  }

  /** Remember a pre-reply ready event without exposing the host to matchmaking. */
  gameReadyObserved(): boolean {
    if (this.canAcceptGameReady()) return true;
    if (this.configuration === true && !this.restorePending && !this.awaitingFreshLaunch &&
        !this.readyConsumed && this.dispatchedLaunch !== null && !this.dispatchedLaunch.confirmed) {
      this.readyBeforeLaunchConfirmation = true;
    }
    return false;
  }

  gameReady(): void {
    if (this.configuration === true && this.readyConsumed) return;
    this.readyConsumed = true;
    this.resetInProgress = false;
    this.sessionDirty = false;
  }

  markSessionDirty(): void {
    if (this.configuration) this.sessionDirty = true;
  }

  shouldResetAfterSession(): boolean {
    return this.sessionDirty && this.configuration === true && !this.resetInProgress;
  }

  /** Unknown configuration remains gated if a disconnect races its lookup. */
  requiresResetBeforeReuse(): boolean {
    return this.configuration !== false;
  }

  beginRequiredReset(): boolean {
    if (this.resetInProgress) return false;
    this.resetInProgress = true;
    this.restorePending = true;
    this.awaitingFreshLaunch = true;
    this.sessionDirty = false;
    this.dispatchedLaunch = null;
    this.readyBeforeLaunchConfirmation = false;
    this.readyConsumed = false;
    this.unconfiguredLaunchComplete = false;
    return true;
  }

  resetForRestart(): void {
    this.configuration = null;
    this.restorePending = true;
    this.awaitingFreshLaunch = true;
    this.dispatchedLaunch = null;
    this.readyBeforeLaunchConfirmation = false;
    this.readyConsumed = false;
    this.unconfiguredLaunchComplete = false;
  }

  clear(): void {
    // Return to the idle/unconfigured default — not `null` (unknown), which
    // would keep readyForPlayers() false and mis-label idle hosts as
    // game_save_restore after boot, campaign clear, or switch failure.
    this.configuration = false;
    this.restorePending = false;
    this.awaitingFreshLaunch = false;
    this.resetInProgress = false;
    this.sessionDirty = false;
    this.dispatchedLaunch = null;
    this.readyBeforeLaunchConfirmation = false;
    this.readyConsumed = false;
    this.unconfiguredLaunchComplete = false;
  }
}
